import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

export type NoteFormMode = "minimized" | "expanded";

export interface OpenNote {
  patientId: string;
  noteId: number | null;
  patientName?: string;
  subjectHint?: string;
  autoRecord?: boolean;
  /**
   * Appointment to link a NEW note to on save (its current live PK; the server
   * resolves it to the stable auditRecordID). Ignored when editing an existing
   * note — link changes go through the appointment modal, not the note form.
   */
  linkAppointmentId?: number;
  /** Display-only chip label, e.g. "Consult · Tue, Aug 12, 2:30 PM". */
  linkAppointmentLabel?: string;
}

export interface RecordingControls {
  start: () => void;
  stop: () => void;
}

export interface NoteFormContextValue {
  mode: NoteFormMode;
  current: OpenNote | null;
  isRecording: boolean;
  isDirty: boolean;
  draftSubject: string;
  openNote: (n: OpenNote) => void;
  close: () => void;
  forceClose: () => void;
  minimize: () => void;
  expand: () => void;
  setIsRecording: (v: boolean) => void;
  setIsDirty: (v: boolean) => void;
  setDraftSubject: (v: string) => void;
  registerRecordingControls: (controls: RecordingControls | null) => void;
  recordingControls: RecordingControls | null;
}

const NoteFormContext = createContext<NoteFormContextValue | null>(null);

export function useNoteForm(): NoteFormContextValue {
  const ctx = useContext(NoteFormContext);
  if (!ctx) {
    throw new Error("useNoteForm must be used within a NoteFormProvider");
  }
  return ctx;
}

export function NoteFormProvider({ children }: { children: React.ReactNode }) {
  const [mode, setMode] = useState<NoteFormMode>("expanded");
  const [current, setCurrent] = useState<OpenNote | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [draftSubject, setDraftSubject] = useState("");
  const recordingControlsRef = useRef<RecordingControls | null>(null);

  const registerRecordingControls = useCallback((controls: RecordingControls | null) => {
    recordingControlsRef.current = controls;
  }, []);

  // Singleton: opening a new note while one is already open prompts the user.
  // If they confirm, we replace; otherwise we keep the current note untouched.
  // Mirrors the call providers' single-session guard but with a recovery path
  // instead of a silent bail.
  const openNote = useCallback((n: OpenNote) => {
    setCurrent((prev) => {
      if (prev) {
        // Appointment context is part of a NEW note's identity: two "new note"
        // requests for the same patient from two different appointments are not the
        // same note, and treating them as one would silently link the note to
        // whichever appointment opened the form first.
        //
        // It is deliberately NOT part of an existing note's identity. For a saved
        // note the appointment context is display-only, so comparing it would make
        // "open note #55 from the Notes tab, then click note #55 in an appointment's
        // linked-notes list" prompt to discard — today that is a silent no-op, and it
        // must stay one.
        const sameNote =
          prev.patientId === n.patientId &&
          prev.noteId === n.noteId &&
          (n.noteId !== null ||
            (prev.linkAppointmentId ?? null) === (n.linkAppointmentId ?? null));
        if (sameNote) {
          return prev;
        }
        const warn = isRecording
          ? "A recording is in progress. Close the current note and discard the recording?"
          : isDirty
            ? "The current note has unsaved changes. Close it and discard them?"
            : "Close the current note and open a new one?";
        if (!window.confirm(warn)) {
          return prev;
        }
      }
      setIsRecording(false);
      setIsDirty(false);
      setDraftSubject(n.subjectHint ?? "");
      setMode("expanded");
      return n;
    });
  }, [isRecording, isDirty]);

  const close = useCallback(() => {
    if (isRecording) {
      if (!window.confirm("A recording is in progress. Close the note and discard the recording?")) return;
    } else if (isDirty) {
      if (!window.confirm("You have unsaved changes. Close without saving?")) return;
    }
    setIsRecording(false);
    setIsDirty(false);
    setDraftSubject("");
    setMode("expanded");
    setCurrent(null);
  }, [isRecording, isDirty]);

  const forceClose = useCallback(() => {
    setIsRecording(false);
    setIsDirty(false);
    setDraftSubject("");
    setMode("expanded");
    setCurrent(null);
  }, []);

  const minimize = useCallback(() => setMode("minimized"), []);
  const expand = useCallback(() => setMode("expanded"), []);

  useEffect(() => {
    if (!current || (!isDirty && !isRecording)) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [current, isDirty, isRecording]);

  const value: NoteFormContextValue = {
    mode,
    current,
    isRecording,
    isDirty,
    draftSubject,
    openNote,
    close,
    forceClose,
    minimize,
    expand,
    setIsRecording,
    setIsDirty,
    setDraftSubject,
    registerRecordingControls,
    recordingControls: recordingControlsRef.current,
  };

  return (
    <NoteFormContext.Provider value={value}>
      {children}
    </NoteFormContext.Provider>
  );
}
